var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Placeholder sourceId that older imports stored on persons with no NK
 * authority record; such a person is unlinked as well.
 */
export var DUMMY_SOURCE_ID = "dummyId";
/** Error parameter carrying the person already holding a duplicate NK identifier. */
var PARAM_CONFLICTING_PERSON_ID = "conflictingPersonId";
/** Error parameter naming the person a merge cannot take as a source (DB-653). */
var PARAM_PERSON_ID = "personId";
/**
 * True when the person is not linked to an NK authority record: its sourceId
 * is blank or the legacy {@link DUMMY_SOURCE_ID} placeholder.
 *
 * @param person the person (only its sourceId is read)
 * @returns whether the person has no NK authority identifier
 */
export var isPersonUnlinked = function (person) {
    var _a;
    var sourceId = (_a = person === null || person === void 0 ? void 0 : person.sourceId) === null || _a === void 0 ? void 0 : _a.trim();
    return !sourceId || sourceId === DUMMY_SOURCE_ID;
};
/**
 * True when the person may be sent to NK authorities: it is unlinked and
 * either kept only in ReČeK (localOnly) or its last send failed. A person
 * already sent without an assigned NK identifier (or a proposal awaiting the
 * nightly sync) may only get its NK identifier entered — a re-send would
 * duplicate the NK record (backend error -84).
 *
 * @param person the person (sourceId, localOnly and authoritySendError are read)
 * @returns whether "Odeslat do NA" applies
 */
export var canSendPerson = function (person) {
    return !!person &&
        isPersonUnlinked(person) &&
        (person.localOnly === true || !!person.authoritySendError);
};
/**
 * True when the person may be merged into another one (disappear): it is
 * unlinked and never reached NK — kept only in ReČeK (localOnly), its last
 * send failed, or it is an import proposal with the {@link DUMMY_SOURCE_ID}
 * placeholder (never sent to NK). Merging a person already sent without an
 * assigned NK identifier would orphan its NK record (backend error -84).
 *
 * @param person the person (sourceId, localOnly and authoritySendError are read)
 * @returns whether the person may be a merge source (DB-653)
 */
export var canMergePerson = function (person) {
    var _a;
    return !!person &&
        (canSendPerson(person) || ((_a = person.sourceId) === null || _a === void 0 ? void 0 : _a.trim()) === DUMMY_SOURCE_ID);
};
var isErrorWithJsonResponse = function (error) {
    var _a;
    return typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof ((_a = error.response) === null || _a === void 0 ? void 0 : _a.json) === "function";
};
/**
 * Reads the error type, the conflicting person id and the rejected merge
 * source from a failed authority link request. The response body is read only once.
 *
 * @param error the value thrown by the request
 * @returns the parsed error; empty when the body is missing or unreadable
 */
export var parsePersonAuthorityLinkError = function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var empty, firstError_1, parameter, _a;
    var _b, _c;
    return __generator(this, function (_d) {
        switch (_d.label) {
            case 0:
                empty = {
                    code: undefined,
                    conflictingPersonId: undefined,
                    personId: undefined,
                };
                if (!isErrorWithJsonResponse(error)) {
                    return [2 /*return*/, empty];
                }
                _d.label = 1;
            case 1:
                _d.trys.push([1, 3, , 4]);
                return [4 /*yield*/, error.response.json()];
            case 2:
                firstError_1 = (_c = (_b = (_d.sent())) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c[0];
                parameter = function (key) { var _a, _b; return (_b = (_a = firstError_1 === null || firstError_1 === void 0 ? void 0 : firstError_1.parameters) === null || _a === void 0 ? void 0 : _a.find(function (p) { return p.key === key; })) === null || _b === void 0 ? void 0 : _b.value; };
                return [2 /*return*/, {
                        code: firstError_1 === null || firstError_1 === void 0 ? void 0 : firstError_1.type,
                        conflictingPersonId: parameter(PARAM_CONFLICTING_PERSON_ID),
                        personId: parameter(PARAM_PERSON_ID),
                    }];
            case 3:
                _a = _d.sent();
                return [2 /*return*/, empty];
            case 4: return [2 /*return*/];
        }
    });
}); };
/** Values of the "Číslo autority NK" register filter (formerly "Napojení na NA"); "" means no filter. */
export var AUTHORITY_LINKED_LINKED = "true";
export var AUTHORITY_LINKED_NOT_LINKED = "false";
/** URL / filter-state key of the "Číslo autority NK" register filter. */
export var AUTHORITY_LINKED_PARAM = "authorityLinked";
/**
 * Keeps only a known filter value (e.g. from a shared URL); anything else
 * means "no filter".
 *
 * @param value the raw value
 * @returns "true", "false" or ""
 */
export var sanitizeAuthorityLinked = function (value) {
    return value === AUTHORITY_LINKED_LINKED || value === AUTHORITY_LINKED_NOT_LINKED
        ? value
        : "";
};
/**
 * CQL clause of the "Číslo autority NK" filter. The backend expects the boolean
 * unquoted (`authorityLinked==true`), so the clause is not built with equals().
 *
 * @param value the filter value
 * @returns the clause, or null when the filter is unset
 */
export var buildAuthorityLinkedClause = function (value) {
    var sanitized = sanitizeAuthorityLinked(value);
    return sanitized ? "".concat(AUTHORITY_LINKED_PARAM, "==").concat(sanitized) : null;
};
